import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { JobService } from "../../services/jobs-service";
import { CountryPreferenceService } from "../../services/country-preference-service";
import { CountryResolutionService } from "../../services/country-resolution-service";
import { LocationService } from "../../services/location-service";
import {
  requireCountrySelection,
  resetClientIp,
  setClientCountry,
  startCountryResolution,
} from "../../store";
import { resetSearchFilter } from "../../store/searchFilterSlice";
import store from "../../store/store";
import { Country, defaultCountrySelection } from "../../types";
import { CountrySelectionModal } from "./country-selection-modal";

interface CountrySelectionContextValue {
  countries: Country[];
  popularCountries: Country[];
  directoryLoading: boolean;
  selectCountry: (country: Country) => void;
}

const CountrySelectionContext = createContext<
  CountrySelectionContextValue | undefined
>(undefined);

interface CountrySelectionProviderProps {
  children: ReactNode;
}

export function CountrySelectionProvider({
  children,
}: CountrySelectionProviderProps) {
  const locationService = useMemo(() => new LocationService(), []);
  const jobService = useMemo(() => new JobService(), []);
  const preferenceService = useMemo(() => new CountryPreferenceService(), []);
  const resolutionService = useMemo(
    () => new CountryResolutionService(locationService),
    [locationService],
  );
  const [countries, setCountries] = useState<Country[]>([]);
  const [jobCounts, setJobCounts] = useState<
    { country: string; jobCount: number }[]
  >([]);
  const [directoryLoading, setDirectoryLoading] = useState(true);
  const [selectionRequired, setSelectionRequired] = useState(false);

  const selectCountry = useCallback(
    (country: Country) => {
      const previous = store.getState().userMetaData.clientCountry;
      if (previous?.code !== country.code) {
        store.dispatch(resetSearchFilter());
      }
      preferenceService.save(country.code);
      store.dispatch(setClientCountry(country));
      setSelectionRequired(false);
    },
    [preferenceService],
  );

  useEffect(() => {
    let active = true;

    store.dispatch(resetClientIp());
    store.dispatch(startCountryResolution());

    const countriesRequest = locationService
      .findCountries(1)
      .then(normalizeCountries)
      .catch((error: unknown) => {
        console.warn("Could not load active countries", error);
        return [];
      });

    void jobService
      .findJobCountByCountry()
      .then((counts) => {
        if (active) setJobCounts(counts);
      })
      .catch((error: unknown) => {
        console.warn("Could not load popular countries", error);
      });

    const preferredCode = preferenceService.read();
    // A saved manual choice is authoritative. Avoid triggering browser
    // geolocation (and its permission prompt) unless that choice is no longer
    // active or does not exist.
    const resolveAutomatically = () =>
      resolutionService.resolveCountry().catch((error: unknown) => {
        console.warn("Automatic country resolution failed", error);
        return null;
      });
    const eagerResolution = preferredCode
      ? null
      : resolveAutomatically();

    void (async () => {
      const activeCountries = await countriesRequest;
      if (!active) return;

      setCountries(activeCountries);
      setDirectoryLoading(false);

      const preferred = activeCountries.find(
        (country) => country.code.trim().toUpperCase() === preferredCode,
      );
      if (preferred) {
        store.dispatch(setClientCountry(preferred));
        return;
      }

      const automaticallyResolved = await (
        eagerResolution ?? resolveAutomatically()
      );
      if (!active) return;

      const canonicalAutomatic = automaticallyResolved
        ? activeCountries.find(
            (country) =>
              country.code.trim().toUpperCase() ===
              automaticallyResolved.code.trim().toUpperCase(),
          ) ?? (activeCountries.length === 0 ? automaticallyResolved : null)
        : null;

      if (canonicalAutomatic) {
        preferenceService.save(canonicalAutomatic.code);
        store.dispatch(setClientCountry(canonicalAutomatic));
        return;
      }

      store.dispatch(requireCountrySelection());
      setSelectionRequired(true);
    })();

    return () => {
      active = false;
    };
  }, [jobService, locationService, preferenceService, resolutionService]);

  const popularCountries = useMemo(() => {
    const byName = new Map(
      countries.map((country) => [country.countryName.toLocaleLowerCase(), country]),
    );
    return [...jobCounts]
      .sort((left, right) => right.jobCount - left.jobCount)
      .map((item) => byName.get(item.country.toLocaleLowerCase()))
      .filter((country): country is Country => Boolean(country))
      .filter(
        (country, index, items) =>
          items.findIndex((item) => item.code === country.code) === index,
      )
      .slice(0, 5);
  }, [countries, jobCounts]);

  const useDefaultCountry = useCallback(() => {
    const activeDefault = countries.find(
      (country) => country.code === defaultCountrySelection.code,
    );
    const fallback = activeDefault ?? defaultCountrySelection;
    preferenceService.save(fallback.code);
    store.dispatch(setClientCountry(fallback));
    setSelectionRequired(false);
  }, [countries, preferenceService]);

  const contextValue = useMemo<CountrySelectionContextValue>(
    () => ({
      countries,
      popularCountries,
      directoryLoading,
      selectCountry,
    }),
    [
      countries,
      directoryLoading,
      popularCountries,
      selectCountry,
    ],
  );

  return (
    <CountrySelectionContext.Provider value={contextValue}>
      {children}
      <CountrySelectionModal
        opened={selectionRequired}
        countries={countries}
        popularCountries={popularCountries}
        loading={directoryLoading}
        onSelect={selectCountry}
        onClose={useDefaultCountry}
      />
    </CountrySelectionContext.Provider>
  );
}

export function useCountrySelection(): CountrySelectionContextValue {
  const context = useContext(CountrySelectionContext);
  if (!context) {
    throw new Error(
      "useCountrySelection must be used inside CountrySelectionProvider",
    );
  }
  return context;
}

function normalizeCountries(countries: Country[]): Country[] {
  const unique = new Map<string, Country>();
  countries
    .filter((country) => country.status === 1 && /^[A-Za-z]{2}$/.test(country.code))
    .forEach((country) => unique.set(country.code.toUpperCase(), country));
  return [...unique.values()].sort((left, right) =>
    left.countryName.localeCompare(right.countryName),
  );
}
